import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import ApiServices from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosClient = AxiosApiCall();

export const useAdminsList = (options = {}) => {
  return useQuery({
    queryKey: ["adminsList"],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getAdmin);
      return response.data?.data || [];
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useAdminById = (adminId, options = {}) => {
  return useQuery({
    queryKey: ["admin", adminId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getAdminById, {
        params: { admin_id: adminId },
      });
      return response.data?.data || null;
    },
    enabled: !!adminId,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

export const useCreateAdmin = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.post(ApiServices.addAdmin, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["adminsList"] });
      toast.success(data?.message || "Admin created successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to create admin";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useUpdateAdmin = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.put(ApiServices.UpdateAdmin, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["adminsList"] });
      queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.success(data?.message || "Admin updated successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error.response?.data?.message || "Failed to update admin";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useAdminActivityLog = (adminId, options = {}) => {
  return useQuery({
    queryKey: ["adminActivityLog", adminId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getAdminActivityLog, {
        params: { admin_id: adminId },
      });
      return response.data?.data || [];
    },
    enabled: !!adminId,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useUpdateAdminTwoFA = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.put(ApiServices.updateAdminTwoFa, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.success(data?.message || "2FA updated successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error.response?.data?.message || "Failed to update 2FA";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};
